export type FontUnit = 'rem' | 'em';
export type ViewportUnit = 'vh' | 'dvh' | 'svh';
export type CSSUnit = 'px' | '%' | FontUnit | ViewportUnit;
export type CSSSize = `${number}${CSSUnit}` | number;

export class CssUnitParser {
  private readonly cache = new Map<string, number>();
  private readonly DEFAULT_ROOT_FONT_SIZE = 16;

  private isValidCSSUnit(unit: string): unit is CSSUnit {
    return ['px', 'vh', 'dvh', 'svh', 'rem', 'em', '%'].includes(unit);
  }

  private isViewportUnit(unit: CSSUnit): unit is ViewportUnit {
    return ['vh', 'dvh', 'svh'].includes(unit);
  }

  private isFontUnit(unit: CSSUnit): unit is FontUnit {
    return ['rem', 'em'].includes(unit);
  }

  private calculateViewportUnit(numValue: number, unit: ViewportUnit): number {
    switch (unit) {
      case 'vh':
        return (numValue / 100) * window.innerHeight;
      case 'dvh':
        return (numValue / 100) * (window.visualViewport?.height || window.innerHeight);
      case 'svh':
        return (numValue / 100) * Math.min(window.innerHeight, window.screen.height);
    }
  }

  private calculateFontUnit(numValue: number, unit: FontUnit, context?: HTMLElement): number {
    switch (unit) {
      case 'rem':
        return numValue * this.getRootFontSize();
      case 'em':
        return numValue * this.getContextFontSize(context);
    }
  }

  private getRootFontSize(): number {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || this.DEFAULT_ROOT_FONT_SIZE;
  }

  private getContextFontSize(context?: HTMLElement): number {
    if (!context) return this.getRootFontSize();
    return parseFloat(getComputedStyle(context).fontSize) || this.DEFAULT_ROOT_FONT_SIZE;
  }

  private getParentHeight(context?: HTMLElement): number {
    if (!context?.parentElement) return window.innerHeight;
    return context.parentElement.clientHeight;
  }

  public toPixel(value: CSSSize, context?: HTMLElement): number {
    if (typeof value === 'number') return value;

    const cacheKey = `${value}-${context?.id || 'default'}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const match = value.match(/^(\d*\.?\d+)(px|vh|dvh|svh|rem|em|%)$/);
    if (!match) {
      console.warn(`Invalid CSS unit: ${value}`);
      return parseFloat(value.toString()) || 0;
    }

    const numberPart = match[1];
    const unit = match[2];

    if (!numberPart || !unit || !this.isValidCSSUnit(unit)) {
      console.warn(`Invalid CSS unit: ${value}`);
      return 0;
    }

    const numValue = parseFloat(numberPart);
    let pixels: number;

    if (unit === 'px') {
      pixels = numValue;
    } else if (this.isViewportUnit(unit)) {
      pixels = this.calculateViewportUnit(numValue, unit);
    } else if (this.isFontUnit(unit)) {
      pixels = this.calculateFontUnit(numValue, unit, context);
    } else if (unit === '%') {
      pixels = (numValue / 100) * this.getParentHeight(context);
    } else {
      pixels = numValue;
    }

    this.cache.set(cacheKey, pixels);
    return pixels;
  }

  public dynamic(value: CSSSize, context?: HTMLElement): () => number {
    return () => {
      if (typeof value === 'string') {
        const match = value.match(/^(\d*\.?\d+)(vh|dvh|svh|%)$/);
        if (match) {
          const numberPart = match[1];
          const unit = match[2];

          if (!numberPart) return 0;

          const numValue = parseFloat(numberPart);

          if (unit === 'vh') {
            return (numValue / 100) * window.innerHeight;
          } else if (unit === 'dvh') {
            return (numValue / 100) * (window.visualViewport?.height || window.innerHeight);
          } else if (unit === 'svh') {
            return (numValue / 100) * Math.min(window.innerHeight, window.screen.height);
          } else if (unit === '%') {
            return (numValue / 100) * this.getParentHeight(context);
          }
        }
      }

      return this.toPixel(value, context);
    };
  }

  public clearCache(): void {
    this.cache.clear();
  }
}
